"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Fraction = exports.Big = exports.Decimal = void 0;
const big_js_1 = __importDefault(require("big.js"));
const decimal_js_light_1 = __importDefault(require("decimal.js-light"));
const jsbi_1 = __importDefault(require("jsbi"));
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const toformat_1 = __importDefault(require("toformat"));
const constants_1 = require("./constants");
const utils_1 = require("./utils");
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
exports.Decimal = (0, toformat_1.default)(decimal_js_light_1.default);
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
exports.Big = (0, toformat_1.default)(big_js_1.default);
const toSignificantRounding = {
    [constants_1.Rounding.ROUND_DOWN]: decimal_js_light_1.default.ROUND_DOWN,
    [constants_1.Rounding.ROUND_HALF_UP]: decimal_js_light_1.default.ROUND_HALF_UP,
    [constants_1.Rounding.ROUND_UP]: decimal_js_light_1.default.ROUND_UP,
};
const toFixedRounding = {
    [constants_1.Rounding.ROUND_DOWN]: 0 /* RoundDown */,
    [constants_1.Rounding.ROUND_HALF_UP]: 1 /* RoundHalfUp */,
    [constants_1.Rounding.ROUND_UP]: 3 /* RoundUp */,
};
/**
 * Number with an integer numerator and denominator.
 */
class Fraction {
    constructor(numerator, denominator = jsbi_1.default.BigInt(1)) {
        /**
         * This boolean checks to see if this is actually a {@link Fraction}.
         */
        this.isFraction = true;
        this.numerator = jsbi_1.default.BigInt((0, utils_1.parseBigintIsh)(numerator));
        this.denominator = jsbi_1.default.BigInt((0, utils_1.parseBigintIsh)(denominator));
    }
    /**
     * Returns true if the other object is a {@link Fraction}.
     *
     * @param other
     * @returns
     */
    static isFraction(other) {
        return (typeof other === "object" &&
            other !== null &&
            "numerator" in other &&
            "denominator" in other);
    }
    /**
     * Parses a {@link Fraction} from a float.
     * @param number Number to parse.
     * @param decimals Number of decimals of precision. (default 10)
     * @returns Fraction
     */
    static fromNumber(number, decimals = 10) {
        const multiplier = Math.pow(10, decimals);
        return new Fraction(Math.floor(number * multiplier), multiplier);
    }
    /**
     * Attempts to parse a {@link Fraction}.
     * @param fractionish Fraction or BigintIsh.
     * @returns
     */
    static tryParseFraction(fractionish) {
        if (Fraction.isFraction(fractionish)) {
            return fractionish;
        }
        try {
            return new Fraction((0, utils_1.parseBigintIsh)(fractionish));
        }
        catch (e) {
            if (e instanceof Error) {
                throw new Error(`Could not parse fraction: ${e.message}`);
            }
            throw new Error(`Could not parse fraction`);
        }
    }
    // performs floor division
    get quotient() {
        return jsbi_1.default.divide(this.numerator, this.denominator);
    }
    // remainder after floor division
    get remainder() {
        return new Fraction(jsbi_1.default.remainder(this.numerator, this.denominator), this.denominator);
    }
    invert() {
        return new Fraction(this.denominator, this.numerator);
    }
    add(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        if (jsbi_1.default.equal(this.denominator, otherParsed.denominator)) {
            return new Fraction(jsbi_1.default.add(this.numerator, otherParsed.numerator), this.denominator);
        }
        return new Fraction(jsbi_1.default.add(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(otherParsed.numerator, this.denominator)), jsbi_1.default.multiply(this.denominator, otherParsed.denominator));
    }
    subtract(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        if (jsbi_1.default.equal(this.denominator, otherParsed.denominator)) {
            return new Fraction(jsbi_1.default.subtract(this.numerator, otherParsed.numerator), this.denominator);
        }
        return new Fraction(jsbi_1.default.subtract(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(otherParsed.numerator, this.denominator)), jsbi_1.default.multiply(this.denominator, otherParsed.denominator));
    }
    lessThan(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        return jsbi_1.default.lessThan(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(otherParsed.numerator, this.denominator));
    }
    equalTo(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        return jsbi_1.default.equal(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(otherParsed.numerator, this.denominator));
    }
    greaterThan(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        return jsbi_1.default.greaterThan(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(otherParsed.numerator, this.denominator));
    }
    multiply(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        return new Fraction(jsbi_1.default.multiply(this.numerator, otherParsed.numerator), jsbi_1.default.multiply(this.denominator, otherParsed.denominator));
    }
    divide(other) {
        const otherParsed = Fraction.tryParseFraction(other);
        return new Fraction(jsbi_1.default.multiply(this.numerator, otherParsed.denominator), jsbi_1.default.multiply(this.denominator, otherParsed.numerator));
    }
    toSignificant(significantDigits, format = { groupSeparator: "" }, rounding = constants_1.Rounding.ROUND_HALF_UP) {
        (0, tiny_invariant_1.default)(Number.isInteger(significantDigits), `${significantDigits} is not an integer.`);
        (0, tiny_invariant_1.default)(significantDigits > 0, `${significantDigits} is not positive.`);
        exports.Decimal.set({
            precision: significantDigits + 1,
            rounding: toSignificantRounding[rounding],
        });
        const quotient = new exports.Decimal(this.numerator.toString())
            .div(this.denominator.toString())
            .toSignificantDigits(significantDigits);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call
        return quotient.toFormat(quotient.decimalPlaces(), format);
    }
    toFixed(decimalPlaces, format = { groupSeparator: "" }, rounding = constants_1.Rounding.ROUND_HALF_UP) {
        (0, tiny_invariant_1.default)(Number.isInteger(decimalPlaces), `${decimalPlaces} is not an integer.`);
        (0, tiny_invariant_1.default)(decimalPlaces >= 0, `${decimalPlaces} is negative.`);
        exports.Big.DP = decimalPlaces;
        exports.Big.RM = toFixedRounding[rounding];
        return new exports.Big(this.numerator.toString()).div(this.denominator.toString()).toFormat(decimalPlaces, format);
    }
    /**
     * Helper method for converting any super class back to a fraction
     */
    get asFraction() {
        return new Fraction(this.numerator, this.denominator);
    }
}
exports.Fraction = Fraction;
Fraction.ZERO = new Fraction(0);
Fraction.ONE = new Fraction(0);
//# sourceMappingURL=fraction.js.map